// The automatic homeowner texts and emails on a deal's Comms tab (Oct 20, 2026).
//
// homeowner_comm_sends holds one row per touch the engine (edge fn homeowner-comms) queued, sent or
// skipped. The Comms tab shows one chip per touch: what went, what waits, and why something didn't
// go. These are the words and the grouping; HomeownerCommsStrip draws them.

import { skipReasonText as reminderSkipText, stopReasonText } from "./autoRemind";

export interface CommSendRow {
  id: string;
  comm_key: string;
  ref: string;
  status: string;
  skip_reason: string | null;
  source: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  sms_status: string | null;
  email_status: string | null;
  rendered: Record<string, unknown> | null;
  error: string | null;
  created_at: string;
}

/** Touch → [short chip label, longer name], in the order a homeowner meets them. */
export const TOUCHES: Array<[string, string, string]> = [
  ["build_date", "T1 Build date", "Build date text"],
  ["t2", "T2 Day before", "Day-before checklist"],
  ["t3", "T3 Build day", "Crew on site this morning"],
  ["t4", "T4 Roof complete", "Roof complete + what happens next"],
  ["t5", "T5 Feedback ask", "How did we do (1–5 stars)"],
  ["t5_nudge", "T5 Nudge", "Feedback nudge"],
  ["t5_result", "T5 Rating reply", "Reply to their rating"],
  ["t6", "T6 Supps sent", "Supplement sent to the carrier"],
  ["t7", "T7 Check guidance", "What to do when the check arrives"],
  ["t8", "T8 Reminder", "Balance reminder (Auto-remind)"],
  ["t9", "T9 Receipt", "Payment received"],
  ["t10", "T10 Paid in full", "Paid in full + statement"],
  ["t11", "T11 Warranty", "Owens Corning warranty certificate"],
  ["t12", "T12 90 days", "Ninety-day check-in + referral"],
  ["ach_verify", "Bank verification", "Bank verification reminder"],
];

const TOUCH_INDEX = new Map(TOUCHES.map(([k], i) => [k, i]));
const TOUCH_NAME = new Map(TOUCHES.map(([k, short, long]) => [k, { short, long }]));

const BUILD_DATE_VARIANTS: Record<string, string> = {
  t1: "Build scheduled",
  t1b: "Build moved",
  t1c: "Build called off today",
};

// A bank payment typed in by hand: ref "<deal_payments id>:<step>" (homeowner-comms v3).
const ACH_VERIFY_STEPS: Record<string, { short: string; long: string }> = {
  start: { short: "Bank verification 1", long: "Bank verification 1 of 3 (one more step)" },
  arrived: { short: "Bank verification 2", long: "Bank verification 2 of 3 (the test deposit is in)" },
  final: { short: "Bank verification 3", long: "Bank verification 3 of 3 (two days before Stripe cancels)" },
  expired: { short: "Bank payment not verified", long: "Bank payment not verified (pay again)" },
};

/** The chip's words for one row: "T8 Reminder 2 (day 14)", "T1 Build moved". */
export function touchLabel(row: Pick<CommSendRow, "comm_key" | "ref" | "rendered">): { short: string; long: string } {
  const base = TOUCH_NAME.get(row.comm_key) ?? { short: row.comm_key, long: row.comm_key.replace(/_/g, " ") };
  if (row.comm_key === "t8") {
    const step = Number(row.ref.split("#")[0].split(":")[1]);
    const n = step === 7 ? 1 : step === 14 ? 2 : step === 21 ? 3 : null;
    if (n) return { short: `T8 Reminder ${n}`, long: `Balance reminder ${n} of 3 (${step} days after the invoice)` };
  }
  if (row.comm_key === "ach_verify") {
    const step = ACH_VERIFY_STEPS[row.ref.split("#")[0].split(":")[1] ?? ""];
    if (step) return step;
  }
  if (row.comm_key === "build_date") {
    const variant = String(row.rendered?.variant ?? "");
    if (BUILD_DATE_VARIANTS[variant]) return { short: `T1 ${BUILD_DATE_VARIANTS[variant]}`, long: BUILD_DATE_VARIANTS[variant] };
  }
  return base;
}

export type Tone = "sent" | "waiting" | "held" | "skipped" | "failed" | "shadow";

/** The chip's state. */
export function statusOf(row: Pick<CommSendRow, "status" | "sms_status" | "email_status">): { tone: Tone; label: string } {
  switch (row.status) {
    case "sent":
      return { tone: "sent", label: "Sent" };
    case "partial":
      return { tone: "sent", label: row.sms_status === "sent" ? "Text sent, email didn't" : "Email sent, text didn't" };
    case "queued":
      return { tone: "waiting", label: "Waiting" };
    case "claimed":
      return { tone: "waiting", label: "Sending" };
    case "held":
      return { tone: "held", label: "Held" };
    case "skipped":
      return { tone: "skipped", label: "Skipped" };
    case "failed":
      return { tone: "failed", label: "Failed" };
    case "shadow":
      return { tone: "shadow", label: "Test only, not sent" };
    default:
      return { tone: "skipped", label: row.status };
  }
}

const SKIP_WORDS: Record<string, string> = {
  superseded: "the build date changed again",
  button_texted: "the Schedule build / Reschedule button already texted them",
  already_told: "they already had this date",
  already_built: "the roof is already built",
  past_date: "the date had passed",
  stage: "the deal is past this step",
  date_changed: "the build date changed",
  window_passed: "its window passed",
  t1_today: "the build text earlier today covered it",
  not_built: "the build isn't marked complete",
  stale_event: "the change was dated too far back",
  already_rated: "they already rated the build",
  no_portal_link: "the street has no number, so there was no portal link",
  no_rating: "there was no rating",
  not_sent: "supps weren't marked sent",
  manual_sent: "Shannon's supps text already went",
  retail: "retail job",
  already_sent: "it already went",
  no_delivery: "the invoice it followed is gone",
  small_balance: "the balance was under $100",
  check_received: "a check was already received",
  payment_missing: "the payment was deleted",
  payment_voided: "the payment was voided",
  stripe: "Stripe sends its own receipt",
  pre_build_payment: "a pre-build payment",
  no_amount: "no amount",
  not_paid: "the balance isn't paid",
  no_payments: "no cleared payments to list",
  no_doc: "the certificate isn't uploaded yet",
  stale_doc: "the certificate was uploaded long ago",
  deal_missing: "the deal was deleted",
  deal_closed: "the deal is closed",
  muted: "automatic texts are muted on this deal",
  disabled: "this text is switched off for everyone",
  opted_out: "the homeowner replied STOP",
  no_contact: "no phone or email on file",
  no_channel: "no valid phone on file",
  burst_limit: "too many of these in an hour; held for a person to check",
  unknown_key: "unknown text",
  bad_ref: "a bad reference",
};

// The bank-verification touch's own reasons; the shared ones (switched off, muted, STOP) come from above.
const ACH_VERIFY_SKIP_WORDS: Record<string, string> = {
  verified: "they verified their bank account",
  not_pending: "the payment wasn't waiting on verification anymore",
  payment_ended: "Stripe cancelled the payment",
  no_verify_link: "Stripe had no verification link for it",
  deadline_passed: "Stripe's deadline to verify had passed",
  staff_texted: "someone on the team texted them the link in the last day",
  not_expired: "the payment didn't time out",
  paid_another_way: "they paid another way since",
};

/** Plain words for why a touch was skipped (or held). */
export function skipText(row: Pick<CommSendRow, "comm_key" | "skip_reason">): string {
  const r = row.skip_reason ?? "";
  if (row.comm_key === "t8") return reminderSkipText(r);
  if (row.comm_key === "ach_verify" && ACH_VERIFY_SKIP_WORDS[r]) return ACH_VERIFY_SKIP_WORDS[r];
  return SKIP_WORDS[r] ?? stopReasonText(r);
}

export interface CommChip {
  key: string;
  /** the row shown: the newest attempt (a resend replaces its original) */
  row: CommSendRow;
  /** how many times it went again from the Comms tab */
  resends: number;
}

/** One chip per touch instance (comm_key + ref without the resend suffix), in touch order, oldest first. */
export function groupChips(rows: CommSendRow[]): CommChip[] {
  const byKey = new Map<string, CommSendRow[]>();
  for (const r of rows) {
    const k = `${r.comm_key}|${r.ref.split("#")[0]}`;
    const list = byKey.get(k);
    if (list) list.push(r);
    else byKey.set(k, [r]);
  }
  const chips: Array<CommChip & { firstAt: number }> = [];
  for (const [key, list] of byKey) {
    list.sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
    chips.push({
      key, row: list[list.length - 1], resends: list.filter((r) => r.ref.includes("#")).length,
      firstAt: Date.parse(list[0].created_at),
    });
  }
  chips.sort((a, b) =>
    (TOUCH_INDEX.get(a.row.comm_key) ?? 99) - (TOUCH_INDEX.get(b.row.comm_key) ?? 99) || a.firstAt - b.firstAt);
  return chips.map(({ key, row, resends }) => ({ key, row, resends }));
}

/** Send now goes for a waiting row; Resend for a finished one. */
export function staffAction(row: Pick<CommSendRow, "status">): "send_now" | "resend" | null {
  if (row.status === "queued" || row.status === "held") return "send_now";
  if (["sent", "partial", "skipped", "failed", "shadow"].includes(row.status)) return "resend";
  return null;
}

/** Who may press Send now / Resend and log a rating (the edge function checks again). */
export const COMMS_STAFF_ROLES = new Set(["admin", "finance"]);
